import {
	MAX_WEIGHT,
	MIN_WEIGHT,
	REQUIRED_TOTAL,
	WEIGHT_SIGNALS,
	WEIGHT_STEP,
} from './healthScoreWeightingConstants';

const TITLE_BY_KEY = WEIGHT_SIGNALS.reduce(
	(titles, { key, title }) => ({ ...titles, [key]: title }),
	{}
);

const signalLabel = (key) => TITLE_BY_KEY[key] ?? key;

const signalList = (keys) =>
	keys
		.split(',')
		.map((key) => signalLabel(key.trim()))
		.filter(Boolean)
		.join(', ');

const GENERIC_MESSAGE = {
	load: 'Could not load the health score weighting. Refresh the page to try again.',
	save: 'Could not save the health score weighting. Try again in a moment.',
};

const STATUS_MESSAGE = {
	401: 'Your session has expired. Sign in again to continue.',
	403: 'You do not have permission to change the health score weighting. Only tenant admins can.',
	404: 'Health score weighting is not available for this tenant yet. Refresh the page to try again.',
	408: 'The request timed out before the weighting was saved. Try again in a moment.',
	429: 'Too many requests in a row. Wait a few seconds and try saving again.',
};

// Rails sends `Tenant#errors.full_messages`, which prefix the humanized column
// name. Matching on the remainder keeps these patterns readable
const ATTRIBUTE_PREFIX = /^Health score weights\s*/;

const VALIDATION_RULES = [
	{
		pattern: /^must total \d+ \(got (\d+)\)$/,
		message: ([total]) =>
			`The weights must total ${REQUIRED_TOTAL}%, but they add up to ${total}%. Adjust a signal and save again.`,
	},
	{
		pattern: /^(\w+) must be between \d+ and \d+$/,
		message: ([key]) =>
			`${signalLabel(key)} must be between ${MIN_WEIGHT}% and ${MAX_WEIGHT}%.`,
	},
	{
		pattern: /^(\w+) must be a multiple of \d+$/,
		message: ([key]) =>
			`${signalLabel(key)} must be a multiple of ${WEIGHT_STEP}%.`,
	},
	{
		pattern: /^(\w+) must be an integer$/,
		message: ([key]) => `${signalLabel(key)} must be a whole number.`,
	},
	{
		pattern: /^is missing justification types: (.+)$/,
		message: ([keys]) =>
			`These signals are missing a weight: ${signalList(keys)}. Refresh the page to try again.`,
	},
	{
		pattern: /^has unknown justification types: (.+)$/,
		message: ([keys]) =>
			`These signals are not recognized: ${signalList(keys)}. Refresh the page to try again.`,
	},
	{
		pattern: /^must be a hash of justification type to weight$/,
		message: () =>
			'The weighting could not be read by the server. Refresh the page to try again.',
	},
];

// Unmatched messages are kept verbatim so an unrelated validation failure on the
// tenant is readable instead of being hidden behind generic copy
const translateValidationError = (rawMessage) => {
	const message = rawMessage.replace(ATTRIBUTE_PREFIX, '');

	for (const { pattern, message: build } of VALIDATION_RULES) {
		const match = message.match(pattern);

		if (match) {
			return build(match.slice(1));
		}
	}

	return rawMessage.endsWith('.') ? rawMessage : `${rawMessage}.`;
};

/**
 * Turns an error thrown by healthScoreWeightingService into copy that explains
 * what happened and what to do next.
 *
 * @param {Error} error - Error from the service, optionally carrying `statusCode`, `errors` and `isNetworkError`.
 * @param {'load'|'save'} action - Which request failed, used for the fallback copy.
 */
export const getHealthScoreWeightingErrorMessage = (error, action = 'save') => {
	const fallback = GENERIC_MESSAGE[action] ?? GENERIC_MESSAGE.save;

	if (error?.isNetworkError) {
		return 'Could not reach the server. Check your connection and try again.';
	}

	const { statusCode, errors = [] } = error ?? {};

	if (statusCode === 422) {
		const details = errors.map(translateValidationError).filter(Boolean);

		return details.length > 0 ? details.join(' ') : fallback;
	}

	if (STATUS_MESSAGE[statusCode]) {
		return STATUS_MESSAGE[statusCode];
	}

	if (statusCode >= 500) {
		return 'The server could not complete the request. Try again in a moment.';
	}

	return fallback;
};
